/**
 * @description Hook utilizado para manipular os tipos de exame
 * @author Diego Sabino Amorim de Araújo
 * @file useTipoExameService.js
 * @license Copyright (c) 2022 SIGEP DIGETI ITEP/RN
 */

import { useDispatch } from 'react-redux'
import actionTypes from 'utils/actionTypes'
import { useFetch } from './useFetch'

export const useTipoExameService = () => {
  const fetch = useFetch()
  const dispatch = useDispatch()

  const listarTiposExames = async (setTiposExames, setProcessando) => {
    try {
      if (setProcessando) setProcessando(false)
      const response = await fetch.get(`tipoExame/detalhados`)
      setTiposExames(response.data.tiposExame)
    } catch (err) {
      return
    } finally {
      if (setProcessando) setProcessando(false)
    }
  }

  const listarTiposExamesAsync = async (setProcessando) => {
    try {
      if (setProcessando) setProcessando(true)
      const response = await fetch.get(`tipoExame/detalhados`)
      return response.data.tiposExame
    } catch (err) {
      return null
    } finally {
      if (setProcessando) setProcessando(false)
    }
  }

  const listarTiposExamesGlobal = async (setProcessando) => {
    try {
      if (setProcessando) setProcessando(true)
      const response = await fetch.get(`tipoExame/detalhados`)
      dispatch({
        type: actionTypes.OPCOES_TIPOS_EXAMES,
        tiposExames: response.data.tiposExame,
      })
      dispatch({
        type: actionTypes.FETCH_EXAMES,
        exames: response.data.tiposExame,
      })
    } catch (err) {
      return
    } finally {
      if (setProcessando) setProcessando(false)
    }
  }

  const listarTipoExameDetalhadoAsync = async (id, setProcessando) => {
    try {
      if (setProcessando) setProcessando(true)
      const response = await fetch.get(`/tipoExame/${id}/detalhado`)
      return response.data.tipoExame
    } catch (err) {
      return null
    } finally {
      if (setProcessando) setProcessando(false)
    }
  }

  const listarTipoExameDetalhado = async (id, setTipoExame, setProcessando) => {
    try {
      if (setProcessando) setProcessando(true)
      const response = await fetch.get(`/tipoExame/${id}/detalhado`)
      setTipoExame(response.data.tipoExame)
    } catch (err) {
      return
    } finally {
      if (setProcessando) setProcessando(false)
    }
  }

  const cadastrarTipoExame = async (dados, setProcessando) => {
    if (setProcessando) setProcessando(true)

    const novoExame = {
      nome: dados.nome,
      setoresResponsaveis: dados.setores,
      quesitos: dados.quesitos,
      agendado: dados.agendado,
      objetosPericia: dados.objetosPericia,
      cod: dados.cod,
      inativado: dados.inativado,
      setorResponsavelNatal: dados.setorResponsavelNatal,
      setorResponsavelMossoro: dados.setorResponsavelMossoro,
      setorResponsavelCaico: dados.setorResponsavelCaico,
      setorResponsavelPDF: dados.setorResponsavelPDF,
    }
    await fetch.postOpen('/tipoExame/', novoExame)

    if (setProcessando) setProcessando(true)
  }

  const editarTipoExame = async (id, dados, setProcessando) => {
    if (setProcessando) setProcessando(true)
    const quesitos = dados.quesitos.map((quesito) => ({
      quesito,
      id_tipo_exame: id,
    }))

    const exame = {
      nome: dados.nome,
      setoresResponsaveis: dados.setores,
      quesitos,
      agendado: dados.agendado,
      objetosPericia: dados.objetosPericia,
      cod: dados.cod,
      inativado: dados.inativado,
      setorResponsavelNatal: dados.setorResponsavelNatal,
      setorResponsavelMossoro: dados.setorResponsavelMossoro,
      setorResponsavelCaico: dados.setorResponsavelCaico,
      setorResponsavelPDF: dados.setorResponsavelPDF,
    }
    await fetch.putOpen(`/tipoExame/${id}/`, exame)
    if (setProcessando) setProcessando(false)
  }

  const deletarTipoExame = async (id, setProcessando) => {
    if (setProcessando) setProcessando(true)
    await fetch.del(`/tipoExame/${id}/`)
    if (setProcessando) setProcessando(false)
  }

  const listarSecaoTipoExameAsync = async (id, setProcessando) => {
    try {
      if (setProcessando) setProcessando(true)
      const response = await fetch.get(`/tipoExame/secaoTipoExame/listar/${id}`)
      return response.data.secaoTipoExame
    } catch (err) {
      return null
    } finally {
      if (setProcessando) setProcessando(false)
    }
  }

  const listarSecoesDeTipoExameAsync = async (idTipoExame, setProcessando) => {
    try {
      if (setProcessando) setProcessando(true)
      const response = await fetch.get(`/tipoExame/secaoTipoExame/listar/deTipoExame/${idTipoExame}`)
      return response.data.secoesTipoExame
    } catch (err) {
      return null
    } finally {
      if (setProcessando) setProcessando(false)
    }
  }

  const editarSecaoTipoExame = async (id, values, setProcessando) => {
    try {
      if (setProcessando) setProcessando(true)
      const dados = {
        titulo: values.titulo,
        textoDefault: values.textoDefault,
      }
      await fetch.putOpen(`/tipoExame/secaoTipoExame/editar/${id}`, dados)
    } catch (err) {
      return
    } finally {
      if (setProcessando) setProcessando(false)
    }
  }

  const cadastrarSecaoTipoExame = async (idTipoExame, values, setProcessando) => {
    try {
      if (setProcessando) setProcessando(true)
      const dados = {
        id_tipo_exame: idTipoExame,
        titulo: values.titulo,
        textoDefault: values.textoDefault,
      }
      await fetch.postOpen(`/tipoExame/secaoTipoExame/cadastrar/`, dados)
    } catch (err) {
      return
    } finally {
      if (setProcessando) setProcessando(false)
    }
  }

  const deletarSecaoTipoExame = async (idSecao, setProcessando) => {
    try {
      if (setProcessando) setProcessando(true)
      await fetch.del(`/tipoExame/secaoTipoExame/deletar/${idSecao}`)
    } catch (err) {
      return
    } finally {
      if (setProcessando) setProcessando(false)
    }
  }

  return {
    listarTiposExames,
    listarTiposExamesAsync,
    listarTiposExamesGlobal,
    listarTipoExameDetalhado,
    listarTipoExameDetalhadoAsync,
    deletarTipoExame,
    cadastrarTipoExame,
    editarTipoExame,
    listarSecaoTipoExameAsync,
    listarSecoesDeTipoExameAsync,
    editarSecaoTipoExame,
    cadastrarSecaoTipoExame,
    deletarSecaoTipoExame,
  }
}
